import {
  ref,
  get,
  set,
  update,
  remove,
  onValue,
  off,
} from 'firebase/database';
import { rtdb } from '../../config/firebase';
import type { HolidayHome } from '../../types';
import { dateToMs, msToDate, snapshotToArray, stripUndefined } from './helpers';

const PATH = 'holidayHomes';

type Unsubscribe = () => void;

const fromRtdb = (id: string, data: any): HolidayHome => ({
  ...data,
  id,
  availability:
    data.availability?.map((block: any) => ({
      ...block,
      startDate: msToDate(block.startDate),
      endDate: msToDate(block.endDate),
    })) || [],
});

const toRtdb = (home: HolidayHome): any => {
  const { id: _id, ...rest } = home;
  return {
    ...rest,
    availability:
      home.availability?.map((block) => ({
        ...block,
        startDate: dateToMs(block.startDate),
        endDate: dateToMs(block.endDate),
      })) || [],
  };
};

export const getAllHomes = async (): Promise<HolidayHome[]> => {
  const snapshot = await get(ref(rtdb, PATH));
  return snapshotToArray<HolidayHome>(snapshot.val(), fromRtdb);
};

export const getHomeById = async (
  homeId: string
): Promise<HolidayHome | null> => {
  const snapshot = await get(ref(rtdb, `${PATH}/${homeId}`));
  return snapshot.exists() ? fromRtdb(homeId, snapshot.val()) : null;
};

export const saveHome = async (home: HolidayHome): Promise<void> => {
  await set(ref(rtdb, `${PATH}/${home.id}`), stripUndefined(toRtdb(home)));
};

export const updateHome = async (
  homeId: string,
  updates: Partial<HolidayHome>
): Promise<void> => {
  const data: any = { ...updates };
  if (updates.availability) {
    data.availability = updates.availability.map((block) => ({
      ...block,
      startDate: dateToMs(block.startDate),
      endDate: dateToMs(block.endDate),
    }));
  }
  await update(ref(rtdb, `${PATH}/${homeId}`), stripUndefined(data));
};

export const deleteHome = async (homeId: string): Promise<void> => {
  await remove(ref(rtdb, `${PATH}/${homeId}`));
};

export const subscribeToHomes = (
  callback: (homes: HolidayHome[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const r = ref(rtdb, PATH);
  const handler = onValue(
    r,
    (snapshot) => {
      callback(snapshotToArray<HolidayHome>(snapshot.val(), fromRtdb));
    },
    onError
  );
  return () => off(r, 'value', handler);
};

export const subscribeToHome = (
  homeId: string,
  callback: (home: HolidayHome | null) => void
): Unsubscribe => {
  const r = ref(rtdb, `${PATH}/${homeId}`);
  const handler = onValue(r, (snapshot) => {
    callback(snapshot.exists() ? fromRtdb(homeId, snapshot.val()) : null);
  });
  return () => off(r, 'value', handler);
};
